/**
 * Redux store shape for the reusable watermark library (VE-24693).
 *
 * Backed by aiWARE Structured Data Objects (SDOs) against the `watermarks` data
 * registry — the same mechanism as Redaction Codes / Settings Profiles. Holds
 * the org-scoped list of watermark definitions users manage on the Settings →
 * Watermarks page, plus the UI state the page and its dialogs share.
 *
 * "Share" is the reason that state is here rather than in the table. Which view
 * is showing and which delete is being confirmed are each read by two
 * components that cannot see each other's local state — the table and the page
 * header, the row menu and the confirmation dialog.
 */
import { StoredWatermarkDefinition } from '../models';

export const namespace = 'vtn-redact-state-watermarks';

export interface WatermarksState {
  watermarkList: StoredWatermarkDefinition[];
  isDrawerOpen: boolean;
  editingWatermarkId?: string;
  deletingWatermarkId?: string;
  /** The open confirmation is for "Permanently delete", not the soft delete. */
  isDeletePermanent: boolean;
  isFetchInProgress: boolean;
  isSaveInProgress: boolean;
  isDeleteInProgress: boolean;
  /**
   * The registry could not be resolved, so the library is running on this
   * browser's storage. Undefined = not yet attempted.
   */
  registryUnavailable?: boolean;
  /** The settings page is showing the deleted view. */
  showDeleted: boolean;
  /**
   * Whether a fetch has ever COMPLETED, success or failure.
   *
   * Distinct from `isFetchInProgress`, which is false in the window between the
   * page mounting and the request going out — and an empty list in that window
   * is indistinguishable from an empty library, so the table flashed "No
   * watermarks yet" on every load before the rows arrived.
   */
  hasFetched: boolean;
}

export const defaultState: WatermarksState = {
  watermarkList: [],
  isDrawerOpen: false,
  editingWatermarkId: undefined,
  deletingWatermarkId: undefined,
  isDeletePermanent: false,
  isFetchInProgress: false,
  isSaveInProgress: false,
  isDeleteInProgress: false,
  registryUnavailable: undefined,
  showDeleted: false,
  hasFetched: false,
};

export interface RedactWatermarksState {
  [namespace]: WatermarksState;
}
